import React, { Component } from 'react'
import { Alert, Input, Modal, Select, Spin, Table, Tag, Typography, message } from 'antd'
import {
  getVibeProjectAuthorization,
  saveVibeProjectAuthorization
} from '../../../request'
import {
  buildProjectAuthorizationGrants,
  getDatasetPolicyMeta,
  isSystemManagedDatasetGrant
} from '../../Profile/vibeDataset'
import { confirmAuthorizationDocumentationWarnings } from '../../vibe/vibeAuthorizationWarning'
import { isTrue } from './operationPermissions'

const { Text } = Typography

// AIC 数据集详情恒为 dataset；保留 projectKind 判定兼容后续 Vibe 应用。
const isDatasetProject = project => (project && project.projectKind ? project.projectKind !== 'application' : true)

const getProjectId = project => (project ? (project.id !== undefined && project.id !== null ? project.id : project.projectId) : null)

/**
 * 数据库授权弹窗（从 Profile/VibeProjectList.js 的 renderAuthorizationModal 抽离）。
 * props：visible / project{id,projectName,projectKind,...} / onClose / onSaved
 * 接口：getVibeProjectAuthorization、saveVibeProjectAuthorization
 */
export default class VibeProjectAuthorizationDialog extends Component {
  state = {
    loading: false,
    loadError: false,
    saving: false,
    project: null,
    users: [],
    selectedUserIds: [],
    permissionTypes: {},
    keyword: ''
  }

  componentDidMount () {
    if (this.props.visible && this.props.project) this.loadAuthorization(this.props.project)
  }

  componentDidUpdate (prevProps) {
    const projectChanged = String(getProjectId(prevProps.project) || '') !== String(getProjectId(this.props.project) || '')
    if (this.props.visible && this.props.project && (!prevProps.visible || projectChanged)) {
      this.loadAuthorization(this.props.project)
    } else if (!this.props.visible && prevProps.visible) {
      this.resetState()
    }
  }

  resetState = () => {
    this.setState({
      loading: false,
      loadError: false,
      saving: false,
      project: null,
      users: [],
      selectedUserIds: [],
      permissionTypes: {},
      keyword: ''
    })
  }

  loadAuthorization = project => {
    const projectId = getProjectId(project)
    this.setState({
      loading: true,
      loadError: false,
      project,
      users: [],
      selectedUserIds: [],
      permissionTypes: {},
      keyword: ''
    })
    if (projectId === null || projectId === undefined || projectId === '') {
      this.setState({ loading: false, loadError: true })
      return
    }
    getVibeProjectAuthorization({ projectId })
      .then(res => {
        const users = Array.isArray(res && res.accounts) ? res.accounts : []
        const projectDetail = { ...project, ...(res || {}) }
        // 新授权默认只读，已有授权回显当前权限，项目所有者固定读写。
        const permissionTypes = users.reduce((result, item) => {
          result[String(item.userId)] = isTrue(item.isOwner) || item.permissionType === 'readwrite'
            ? 'readwrite'
            : 'readonly'
          return result
        }, {})
        this.setState({
          project: projectDetail,
          users,
          permissionTypes,
          selectedUserIds: users
            .filter(item => (
              isTrue(item.authorized)
              || isTrue(item.isOwner)
              || (isDatasetProject(projectDetail) && isSystemManagedDatasetGrant(item, true))
            ))
            .map(item => String(item.userId))
        })
      })
      .catch(() => this.setState({ loadError: true }))
      .finally(() => this.setState({ loading: false }))
  }

  close = () => {
    if (this.state.saving) return
    this.resetState()
    if (this.props.onClose) this.props.onClose()
  }

  save = () => {
    const {
      project,
      users,
      selectedUserIds,
      permissionTypes,
      loading,
      loadError
    } = this.state
    const projectId = getProjectId(project)
    if (!projectId || loading || loadError) return
    const datasetProject = isDatasetProject(project)
    const grants = buildProjectAuthorizationGrants(selectedUserIds, users, datasetProject, permissionTypes)
    const selectedIds = new Set(selectedUserIds.map(String))
    const databasePermissionIncreased = users.some(item => {
      const userId = String(item.userId)
      if (!selectedIds.has(userId) || isTrue(item.isOwner) || isSystemManagedDatasetGrant(item, datasetProject)) {
        return false
      }
      const wasAuthorized = isTrue(item.authorized)
      const previousPermission = item.permissionType === 'readwrite' ? 'readwrite' : 'readonly'
      const nextPermission = permissionTypes[userId] === 'readwrite' ? 'readwrite' : 'readonly'
      return !wasAuthorized || (previousPermission === 'readonly' && nextPermission === 'readwrite')
    })
    const databaseProjects = databasePermissionIncreased
      && Number(project && project.databaseReadmeLength || 0) <= 0
      ? [project && (project.projectName || project.projectSlug) || '当前项目']
      : []
    confirmAuthorizationDocumentationWarnings({ databaseProjects }, () => (
      this.executeSaveAuthorization(projectId, grants)
    ))
  }

  executeSaveAuthorization = (projectId, grants) => {
    this.setState({ saving: true })
    saveVibeProjectAuthorization({ projectId, grants })
      .then(() => {
        message.success('授权保存成功')
        this.resetState()
        if (this.props.onSaved) this.props.onSaved()
        if (this.props.onClose) this.props.onClose()
      })
      .catch(() => {})
      .finally(() => this.setState({ saving: false }))
  }

  render () {
    const {
      loading,
      loadError,
      saving,
      project,
      users,
      selectedUserIds,
      permissionTypes,
      keyword
    } = this.state
    const normalizedKeyword = keyword.trim().toLowerCase()
    const filteredUsers = normalizedKeyword
      ? users.filter(item => (
        [item.userNick, item.userName, item.userId, item.mysqlUsername]
          .some(value => String(value || '').toLowerCase().includes(normalizedKeyword))
      ))
      : users
    const sourceProject = project || this.props.project || {}
    const datasetProject = isDatasetProject(sourceProject)
    const policy = getDatasetPolicyMeta(sourceProject.sensitivityLevel)
    return <Modal
      title={datasetProject ? 'AIC项目授权' : 'Vibe项目授权'}
      visible={this.props.visible}
      width={980}
      confirmLoading={saving}
      okButtonProps={{ disabled: loading || loadError }}
      okText="保存授权"
      cancelText="取消"
      onOk={this.save}
      onCancel={this.close}
      closable={!saving}
      maskClosable={!saving}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {loadError
          ? <Alert type="error" showIcon message="授权人员加载失败，请关闭弹窗后重试。" style={{ marginBottom: 12 }} />
          : datasetProject
            ? <Alert
              showIcon
              type={sourceProject.sensitivityLevel === 'L3' ? 'error' : 'info'}
              message={policy.text}
              description={`${policy.summary} 系统默认授权由敏感级和一级部门自动维护，不能在这里取消；手工授权可选择只读或读写权限。`}
              style={{ marginBottom: 12 }}
            />
            : <div style={{ marginBottom: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
              可选择全部有效用户并分别设置只读或读写权限。
            </div>}
        <Input.Search
          allowClear
          value={keyword}
          placeholder="搜索姓名、登录账号或 Vibe 账号"
          onChange={event => this.setState({ keyword: event.target.value })}
          style={{ width: 320, marginBottom: 12 }}
        />
        <Table
          rowKey={record => String(record.userId)}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          dataSource={filteredUsers}
          locale={{ emptyText: '暂无可授权人员' }}
          rowSelection={{
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedUserIds,
            onChange: keys => this.setState({ selectedUserIds: keys.map(String) }),
            getCheckboxProps: record => ({
              disabled: isTrue(record.isOwner)
                || (datasetProject && isSystemManagedDatasetGrant(record, true))
                || (!isTrue(record.spaceEnabled) && !isTrue(record.canProvision))
            })
          }}
          columns={[
            {
              title: '人员',
              key: 'user',
              render: (_, record) => (
                <div>
                  <div>
                    {record.userNick || record.userName || record.userId || '-'}
                    {isTrue(record.isOwner) ? <Tag color="blue" style={{ marginLeft: 8 }}>项目所有者</Tag> : null}
                    {datasetProject && isSystemManagedDatasetGrant(record, true)
                      ? <Tag color="cyan" style={{ marginLeft: 8 }}>系统默认</Tag>
                      : null}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.userName || record.userId || '-'}
                  </Text>
                </div>
              )
            },
            {
              title: 'Vibe空间',
              key: 'spaceStatus',
              width: 120,
              render: (_, record) => {
                if (isTrue(record.spaceEnabled)) return <Tag color="green">已开通</Tag>
                if (record.accountStatus === 'disabled') return <Tag color="red">已停用</Tag>
                if (isTrue(record.canProvision)) return <Tag color="gold">未开通</Tag>
                if (record.accountStatus) return <Tag color="red">账号异常</Tag>
                return <Tag>缺少公司邮箱</Tag>
              }
            },
            {
              title: 'Vibe账号',
              dataIndex: 'mysqlUsername',
              width: 160,
              render: (value, record) => (
                value
                  ? <Text code>{value}</Text>
                  : <Text type="secondary">{isTrue(record.canProvision) ? '授权时创建' : '-'}</Text>
              )
            },
            {
              title: '当前权限',
              dataIndex: 'permissionType',
              width: 100,
              render: (value, record) => (
                isTrue(record.authorized)
                  ? <Tag color="green">{value === 'readonly' ? '只读' : '读写'}</Tag>
                  : <Text type="secondary">未授权</Text>
              )
            },
            {
              title: '授权权限',
              key: 'grantPermissionType',
              width: 120,
              render: (_, record) => {
                const userId = String(record.userId)
                const systemManaged = datasetProject && isSystemManagedDatasetGrant(record, true)
                if (isTrue(record.isOwner) || systemManaged) {
                  const permissionType = isTrue(record.isOwner)
                    ? 'readwrite'
                    : (record.permissionType || 'readonly')
                  return (
                    <Tag color={permissionType === 'readonly' ? 'blue' : 'green'}>
                      {permissionType === 'readonly' ? '只读' : '读写'}
                    </Tag>
                  )
                }
                return (
                  <Select
                    size="small"
                    value={permissionTypes[userId] || 'readonly'}
                    disabled={!selectedUserIds.includes(userId)}
                    style={{ width: 88 }}
                    onChange={permissionType => this.setState(prevState => ({
                      permissionTypes: {
                        ...prevState.permissionTypes,
                        [userId]: permissionType
                      }
                    }))}
                  >
                    <Select.Option value="readonly">只读</Select.Option>
                    <Select.Option value="readwrite">读写</Select.Option>
                  </Select>
                )
              }
            },
            ...(datasetProject ? [{
              title: '授权来源',
              key: 'grantSource',
              width: 110,
              render: (_, record) => (
                isSystemManagedDatasetGrant(record, true)
                  ? <Tag color="cyan">系统策略</Tag>
                  : (isTrue(record.authorized)
                    ? <Tag color="blue">手工授权</Tag>
                    : <Text type="secondary">-</Text>)
              )
            }] : [])
          ]}
        />
      </Spin>
    </Modal>
  }
}
